import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar.js";
import Header from "./Header.js";
import ChatBubble from "../chat/ChatBubble.js";
import { ScreenMetaProvider } from "../chat/ScreenMetaContext.js";
import { AiConsoleSlotProvider } from "../chat/AiConsoleSlotContext.js";
import { AiModeProvider, useAiMode } from "../AiModeContext.js";
import { ConfirmDialogProvider } from "./ConfirmDialog.js";

/** Inner shell — needs to be a child of AiModeProvider so it can read
 *  the current mode to decide whether to render the floating ChatBubble.
 *  Spec docs/specs/ai-mode-operations-console.md §1: AI mode hides the
 *  floating bubble because the AI 導覽 page embeds its own chat panel. */
function AppLayoutInner(): React.ReactElement {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { mode } = useAiMode();

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // AI 導覽 (`/dashboard/topology` in AI mode) renders chat as the
  // central workspace and needs to fill main edge-to-edge with the
  // composer pinned at the bottom + header pinned at the top + only
  // the messages list scrolling — classic chat-app shape. The default
  // padded `mx-auto max-w-[1400px] px-* py-6` wrapper would force the
  // whole page to scroll (wrapper height = content + 48px padding ≫
  // main height) and the chat header / composer would slide off-screen
  // with the body scroll. Skip the wrapper entirely on that route +
  // mode combo and flip main to a constrained flex column so the
  // ChatBubble's own `flex-1 min-h-0` messages container is the sole
  // scroll affordance.
  //
  // Spec docs/specs/ai-mode-operations-console.md §4.5.
  const fullBleed = mode === "ai" && location.pathname === "/dashboard/topology";

  // Mobile Safari leaks page scroll past root `overflow-hidden` because
  // `<body>` itself scrolls when its child overflows the visible
  // viewport. `h-screen` (= 100vh) is the LARGE viewport height (URL
  // bar collapsed), so on first paint the page is ~56px taller than
  // visible → body scrolls → chat header / composer get dragged with
  // the scroll. Two-step fix: use `100dvh` so the outer matches the
  // ACTUAL visible viewport, AND lock body overflow while in
  // fullBleed mode so even small layout drift can't trigger body
  // scroll. The body-style toggle reverts on cleanup so unrelated
  // pages keep their normal scroll behaviour.
  useEffect(() => {
    if (!fullBleed) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [fullBleed]);

  return (
    <div className={fullBleed
      ? "flex h-[100dvh] bg-bg-base text-text-primary overflow-hidden"
      : "flex min-h-screen bg-bg-base text-text-primary"}>
      {/* Skip-to-content for keyboard */}
      <a
        href="#dashboard-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:btn-primary focus:text-sm"
      >
        跳到主要內容
      </a>
      <Sidebar variant="desktop" />
      <Sidebar variant="drawer" open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex flex-1 flex-col min-w-0 min-h-0">
        <Header onOpenSidebar={() => setDrawerOpen(true)} />
        <main
          id="dashboard-main"
          className={fullBleed
            ? "flex-1 overflow-hidden flex flex-col min-h-0"
            : "flex-1 overflow-x-hidden"}
          tabIndex={-1}
        >
          {fullBleed ? (
            <Outlet />
          ) : (
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 animate-fade-in">
              <Outlet />
            </div>
          )}
        </main>
      </div>
      {/* Floating SENTRY 助手. AI mode now seamlessly navigates to any
          dashboard page (Q-c). On /topology in AI mode the chat is the
          central workspace — no floating bubble. Elsewhere in AI mode
          the floating bubble keeps chat accessible without forcing the
          operator back to /topology. Professional mode behaves as
          before: floating bubble always visible. */}
      {(mode === "professional" || !fullBleed) && <ChatBubble />}
    </div>
  );
}

export default function AppLayout(): React.ReactElement {
  return (
    <ConfirmDialogProvider>
      <ScreenMetaProvider>
        <AiModeProvider>
          <AiConsoleSlotProvider>
            <AppLayoutInner />
          </AiConsoleSlotProvider>
        </AiModeProvider>
      </ScreenMetaProvider>
    </ConfirmDialogProvider>
  );
}
